import React, { SyntheticEvent } from 'react';
import MUISnackbar, { SnackbarCloseReason } from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';
import ErrorIcon from '@material-ui/icons/Error';
import DoneIcon from '@material-ui/icons/Done';
import { useDispatch } from 'react-redux';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core';
import { clearSnackbar, SnackbarStatus } from 'store/slices/snackbar';
import Typography from 'components/Typography';
import { useSnackbarState } from 'store/hooks';
import NewSnackbar from './NewSnackbar';

const useStyles = makeStyles(
  theme => ({
    snackbar: {
      padding: theme.spacing(1, 1, 1, 2),
      color: theme.palette.common.white,
      backgroundColor: (props: AlertProps) => {
        if (props.severity === SnackbarStatus.Success) {
          return theme.palette.aegis.semantic.primary.base;
        }
        if (props.severity === SnackbarStatus.Error) {
          return theme.palette.aegis.semantic.error.base;
        }
        if (props.severity === SnackbarStatus.Warning) {
          return theme.palette.aegis.semantic.message.black;
        }
        if (props.severity === SnackbarStatus.Info) {
          return theme.palette.aegis.semantic.message.black;
        }
        return theme.palette.aegis.semantic.primary.base;
      }
    },
    iconWrapper: {
      alignItems: 'center'
    },
    dismissButton: {
      padding: theme.spacing(1, 2)
    }
  }),
  { name: 'Snackbar' }
);

function Alert(props: AlertProps) {
  const classes = useStyles(props);

  return (
    <MuiAlert
      elevation={6}
      variant="filled"
      className={classes.snackbar}
      classes={{
        icon: classes.iconWrapper
      }}
      {...props}
    />
  );
}

export default function Snackbar() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    title,
    message,
    show,
    status,
    redirectAction,
    useNewSnackbar
  } = useSnackbarState();

  const autoHideDurationInMs = 6000;

  const onClose = (
    _: SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(clearSnackbar());
  };

  const handleRedirectAction = () => {
    return window.open(
      `${window.location.origin}/${redirectAction?.url}`,
      redirectAction?.newTab ? '_blank' : '_top'
    );
  };

  const alertActionButton = (
    <Button
      data-testid="snackbar-action"
      color="inherit"
      size="small"
      className={classes.dismissButton}
      onClick={redirectAction ? handleRedirectAction : onClose}
    >
      {redirectAction ? redirectAction.title : 'Dismiss'}
    </Button>
  );
  return useNewSnackbar ? (
    <NewSnackbar
      show={show}
      title={title}
      message={message}
      status={status}
      autoHideDurationInMs={autoHideDurationInMs}
      onClose={onClose}
    />
  ) : (
    <MUISnackbar
      open={show}
      autoHideDuration={autoHideDurationInMs}
      onClose={onClose}
    >
      <Alert
        data-qa-id="snackbar"
        data-testid="snackbar"
        action={alertActionButton}
        severity={status == null ? SnackbarStatus.Info : status}
        iconMapping={{
          success: <DoneIcon fontSize="inherit" />,
          error: <ErrorIcon fontSize="inherit" />,
          warning: <DoneIcon fontSize="inherit" />,
          info: <DoneIcon fontSize="inherit" />
        }}
      >
        {!title ? null : (
          <AlertTitle>
            <Typography variant="h3" component="div">
              {title}
            </Typography>
          </AlertTitle>
        )}
        <Typography>{message}</Typography>
      </Alert>
    </MUISnackbar>
  );
}
